import { useEffect, useState } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initialValue;

    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setLocalStorage = (newValue: T | ((prev: T) => T)) => {
    if (typeof window === 'undefined') return;

    setStoredValue((prev) => {
      const valueToStore =
        newValue instanceof Function ? newValue(prev) : newValue;
      try {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (err) {
        console.warn(`Failed to write "${key}" to localStorage:`, err);
      }
      return valueToStore;
    });
  };

  const removeLocalStorage = () => {
    if (typeof window === 'undefined') return;

    try {
      window.localStorage.removeItem(key);
    } catch (err) {
      console.warn(`Failed to remove "${key}" from localStorage:`, err);
    }
    setStoredValue(initialValue);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorage = (e: StorageEvent) => {
      if (e.key === key) {
        try {
          setStoredValue(e.newValue ? JSON.parse(e.newValue) : initialValue);
        } catch {
          setStoredValue(initialValue);
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [key, initialValue]);

  return { storedValue, setLocalStorage, removeLocalStorage };
}

export function useLocalStorageArray<T>(key: string, initialValue: T[] = []) {
  const { storedValue, setLocalStorage, removeLocalStorage } = useLocalStorage<
    T[]
  >(key, initialValue);

  const push = (item: T) => setLocalStorage((prev) => [...prev, item]);

  const removeAt = (index: number) =>
    setLocalStorage((prev) => prev.filter((_, i) => i !== index));

  const updateAt = (index: number, item: T) =>
    setLocalStorage((prev) => prev.map((v, i) => (i === index ? item : v)));

  const clear = () => setLocalStorage([]);

  const removeWhere = (predicate: (item: T) => boolean) =>
    setLocalStorage((prev) => prev.filter((item) => !predicate(item)));

  const length = storedValue.length;
  const isEmpty = length === 0;

  return {
    storedValue,
    setLocalStorage,
    removeLocalStorage,
    push,
    removeAt,
    updateAt,
    clear,
    removeWhere,
    length,
    isEmpty,
  };
}
